import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { S3Avatar, S3Badge, S3EmptyState, S3Modal } from './v3/primitives';
import { initialsOf } from './constants';
import { photoUrl } from '../../utils/photoUrl';
import type { Assignee, AssigneeRef, Candidate } from '../../services/supportApi';
// Le modali sono condivise con l'area rappresentante, che non passa dalla
// shell admin ne dalla pagina del Centro Ticket: i due fogli scoped (.adm2 /
// .sup3, entrambi portati da S3Modal sulla radice) vanno importati qui.
import '../../styles/admin-v2.css';
import '../../styles/support-v3.css';


/** Chiave stabile della coppia attore: gli id possono collidere fra collezioni. */
export const assigneeKey = (c: { actorType: string; actorId: string }) => `${c.actorType}:${c.actorId}`;

/**
 * Diff fra gli assegnatari correnti (`isAssigned` dei candidati) e la selezione
 * dell'operatore. Funzione pura: e la sola logica non banale della modale.
 */
export function diffAssignees(
  candidates: Candidate[],
  selected: Set<string>
): { add: Assignee[]; remove: Assignee[] } {
  const assignedNow = new Set(candidates.filter((c) => c.isAssigned).map(assigneeKey));
  const pair = (c: Candidate): Assignee => ({ actorType: c.actorType, actorId: c.actorId });
  return {
    add: candidates.filter((c) => selected.has(assigneeKey(c)) && !assignedNow.has(assigneeKey(c))).map(pair),
    remove: candidates.filter((c) => !selected.has(assigneeKey(c)) && assignedNow.has(assigneeKey(c))).map(pair),
  };
}

/** Assegnatario esterno gia sul ticket → riga della sezione esterni (spuntata). */
const externalRow = (a: AssigneeRef): Candidate => ({
  actorType: a.actorType,
  actorId: a.actorId,
  nome: a.nome,
  cognome: '',
  foto: a.foto,
  isAssigned: true,
  external: true,
});

/** Ricerca degli esterni: minimo di caratteri e attesa dopo l'ultima battuta. */
export const EXTERNAL_MIN_CHARS = 2;
export const EXTERNAL_DEBOUNCE_MS = 300;

interface AssigneesModalProps {
  candidates: Candidate[];
  busy?: boolean;
  onClose: () => void;
  /** Esegue la chiamata e la gestione errori/toast, poi rifetcha. */
  onSave: (payload: { add: Assignee[]; remove: Assignee[] }) => void | Promise<void>;
  /**
   * Capability `canAssignExternal` (Task 26, solo admin con
   * `supporttickets.assign_external`): ricerca degli operatori FUORI dal canale.
   * Assente = la sezione «Altri operatori» non esiste.
   */
  searchExternal?: (q: string) => Promise<Candidate[]>;
  /**
   * Assegnatari attuali esterni al canale (`external: true` sul ticket): con la
   * capability compaiono spuntati nella sezione esterni, cosi si possono togliere.
   */
  externalAssigned?: AssigneeRef[];
}

const CandidateRow: React.FC<{ c: Candidate; on: boolean; onToggle: () => void }> = ({ c, on, onToggle }) => (
  <label className={`s3-pick__opt${on ? ' s3-pick__opt--on' : ''}`}>
    <input type="checkbox" checked={on} onChange={onToggle} />
    <span className="s3-pick__txt s3-pick__txt--row">
      {/* Avatar sempre presente, della stessa misura: foto per i
          rappresentanti, iniziali per gli utenti admin (che non ne
          hanno). Cosi le righe della lista restano allineate. */}
      <S3Avatar
        initials={initialsOf(`${c.nome} ${c.cognome || ''}`)}
        size={20}
        src={photoUrl(c.foto)}
        title={`${c.nome} ${c.cognome || ''}`.trim()}
      />
      {c.nome} {c.cognome || ''}
      {c.actorType === 'user' && <S3Badge tone="chip" small>admin</S3Badge>}
      {c.external && (
        <S3Badge tone="vio" small title="Esterno al canale: vedrà solo questo ticket">
          Esterno
        </S3Badge>
      )}
    </span>
  </label>
);

// Modal «Componenti assegnati»: seleziona gli operatori del canale (rappresentanti
// e utenti admin) e calcola il diff add/remove in coppie { actorType, actorId }.
// Resa V3 (T11): S3Modal porta `.adm2 .sup3` sulla radice, quindi la modale ha
// lo stesso linguaggio anche fuori dalla shell admin (area rappresentante).
// Task 26: con `searchExternal` una seconda sezione cerca operatori fuori dal
// canale; gli esterni scelti finiscono nello stesso payload `add`.
const AssigneesModal: React.FC<AssigneesModalProps> = ({
  candidates,
  busy = false,
  onClose,
  onSave,
  searchExternal,
  externalAssigned = [],
}) => {
  // Esterni noti alla modale: quelli gia assegnati + quelli toccati dalla ricerca.
  const [externals, setExternals] = useState<Candidate[]>(() =>
    searchExternal ? externalAssigned.map(externalRow) : []
  );
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set([...candidates, ...(searchExternal ? externalAssigned.map(externalRow) : [])]
      .filter((c) => c.isAssigned)
      .map(assigneeKey))
  );
  const [query, setQuery] = useState<string>('');
  const [results, setResults] = useState<Candidate[]>([]);
  const [searching, setSearching] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<boolean>(false);
  const seq = useRef(0);
  // In una ref: il chiamante puo passare una funzione nuova a ogni render senza
  // far ripartire la ricerca.
  const searchRef = useRef(searchExternal);
  searchRef.current = searchExternal;
  const canSearch = Boolean(searchExternal);

  useEffect(() => {
    const search = searchRef.current;
    if (!canSearch || !search) return undefined;
    const q = query.trim();
    if (q.length < EXTERNAL_MIN_CHARS) {
      seq.current += 1;
      setResults([]);
      setSearching(false);
      setSearchError(false);
      return undefined;
    }
    const mine = ++seq.current;
    const timer = setTimeout(() => {
      setSearching(true);
      setSearchError(false);
      search(q)
        .then((rows) => { if (seq.current === mine) setResults(rows.map((r) => ({ ...r, external: true }))); })
        .catch(() => { if (seq.current === mine) { setResults([]); setSearchError(true); } })
        .finally(() => { if (seq.current === mine) setSearching(false); });
    }, EXTERNAL_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, canSearch]);

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Un risultato della ricerca spuntato entra fra gli esterni noti: resta nella
  // lista anche se la ricerca cambia, e il diff lo vede.
  const toggleExternal = (c: Candidate) => {
    const key = assigneeKey(c);
    setExternals((prev) => (prev.some((x) => assigneeKey(x) === key) ? prev : [...prev, c]));
    toggle(key);
  };

  const save = () => onSave(diffAssignees([...candidates, ...externals], selected));

  const known = new Set(externals.map(assigneeKey));
  const freshResults = results.filter((r) => !known.has(assigneeKey(r)));

  return (
    <S3Modal open onClose={onClose} labelledBy="s3-assignees-title">
      <div className="s3-mod">
        <div className="s3-mod__head">
          <h3 className="s3-h3" id="s3-assignees-title">Componenti assegnati</h3>
          <button
            type="button"
            className="s3-pill s3-pill--sm s3-pill--icon"
            onClick={onClose}
            aria-label="Chiudi"
          >
            <X size={14} />
          </button>
        </div>

        <div className="s3-mod__body">
          {candidates.length === 0 ? (
            <S3EmptyState>Nessun candidato disponibile.</S3EmptyState>
          ) : (
            <div className="s3-pick">
              {candidates.map((c) => {
                const key = assigneeKey(c);
                return <CandidateRow key={key} c={c} on={selected.has(key)} onToggle={() => toggle(key)} />;
              })}
            </div>
          )}

          {searchExternal && (
            <section className="s3-field" style={{ marginTop: 14 }} aria-label="Altri operatori (fuori dal canale)">
              <span className="s3-label">Altri operatori (fuori dal canale)</span>
              <input
                type="search"
                className="s3-search s3-search--plain"
                placeholder="Cerca per nome, cognome o email (min 2 caratteri)"
                aria-label="Cerca altri operatori"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <span className="s3-pick__sub">L&apos;operatore esterno vedrà solo questo ticket, non il resto del canale.</span>
              {(externals.length > 0 || freshResults.length > 0) && (
                <div className="s3-pick">
                  {[...externals, ...freshResults].map((c) => {
                    const key = assigneeKey(c);
                    return <CandidateRow key={key} c={c} on={selected.has(key)} onToggle={() => toggleExternal(c)} />;
                  })}
                </div>
              )}
              {searching && <span className="s3-pick__sub">Ricerca…</span>}
              {searchError && <span className="s3-pick__sub">Ricerca non riuscita, riprova.</span>}
              {!searching && !searchError && query.trim().length >= EXTERNAL_MIN_CHARS && freshResults.length === 0 && results.length === 0 && (
                <span className="s3-pick__sub">Nessun operatore trovato.</span>
              )}
            </section>
          )}
        </div>

        <div className="s3-mod__foot">
          <button type="button" className="s3-pill" onClick={onClose}>
            Annulla
          </button>
          <button type="button" className="s3-pill s3-pill--primary" onClick={save} disabled={busy}>
            Salva
          </button>
        </div>
      </div>
    </S3Modal>
  );
};

export default AssigneesModal;
